import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { MAX_ZOOM, MIN_ZOOM } from '../components/views/FebView/constants';
import { ICameraManager } from './interfaces';

export class CameraManager implements ICameraManager {
  /**
   * Zoom in the camera with constraints
   */
  public zoomIn(camera: THREE.Camera, controls?: OrbitControls): void {
    if (!camera || !(camera instanceof THREE.OrthographicCamera)) return;

    const orthoCamera = camera as THREE.OrthographicCamera;
    const newZoom = Math.min(orthoCamera.zoom * 1.2, MAX_ZOOM);
    orthoCamera.zoom = newZoom;
    orthoCamera.updateProjectionMatrix();

    if (controls) {
      controls.update();
    }
  }

  /**
   * Zoom out the camera with constraints
   */
  public zoomOut(camera: THREE.Camera, controls?: OrbitControls): void {
    if (!camera || !(camera instanceof THREE.OrthographicCamera)) return;

    const orthoCamera = camera as THREE.OrthographicCamera;
    const newZoom = Math.max(orthoCamera.zoom * 0.8, MIN_ZOOM);
    orthoCamera.zoom = newZoom;
    orthoCamera.updateProjectionMatrix();

    if (controls) {
      controls.update();
    }
  }

  /**
   * Zoom camera to fit the floor plane content
   */
  public zoomToFit(
    camera: THREE.Camera,
    controls: OrbitControls,
    floorPlane: THREE.Mesh,
    container: HTMLElement
  ): void {
    if (!camera || !controls || !floorPlane || !container) return;
    if (!(camera instanceof THREE.OrthographicCamera)) return;

    // Calculate the bounding box of the floor plane
    const floorBounds = new THREE.Box3().setFromObject(floorPlane);
    const floorSize = new THREE.Vector3();
    floorBounds.getSize(floorSize);

    // Get container aspect ratio
    const containerAspect = container.clientWidth / container.clientHeight;

    // Calculate zoom level needed to fit the floor
    const orthoCamera = camera as THREE.OrthographicCamera;

    // Calculate the zoom level needed based on floor dimensions and container aspect
    const floorAspect = floorSize.x / floorSize.z;
    let zoom = 1;

    if (containerAspect > floorAspect) {
      // Container is wider than floor - fit to floor height
      zoom = 1 / (floorSize.z / 15); // 15 is the frustumSize from threeSetup.ts
    } else {
      // Container is taller than floor - fit to floor width
      zoom = 1 / (floorSize.x / (15 * containerAspect));
    }

    // Apply zoom with a small margin and respect zoom limits
    orthoCamera.zoom = Math.min(Math.max(zoom * 0.9, MIN_ZOOM), MAX_ZOOM);
    orthoCamera.updateProjectionMatrix();

    // Center the camera on the floor
    const center = new THREE.Vector3();
    floorBounds.getCenter(center);
    controls.target.set(center.x, 0, center.z);
    controls.update();
  }

  /**
   * Reset camera to default position and zoom
   */
  public resetCamera(camera: THREE.Camera, controls: OrbitControls): void {
    if (!camera || !controls) return;
    if (!(camera instanceof THREE.OrthographicCamera)) return;

    const orthoCamera = camera as THREE.OrthographicCamera;

    // Reset camera position and zoom to initial values
    orthoCamera.position.set(0, 10, 0);
    orthoCamera.lookAt(0, 0, 0);
    orthoCamera.zoom = 2;
    orthoCamera.updateProjectionMatrix();

    // Reset controls target
    controls.target.set(0, 0, 0);
    controls.update();
  }

  /**
   * Take a screenshot of the current scene
   */
  public takeScreenshot(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    filename?: string
  ): void {
    if (!renderer || !scene || !camera) return;

    // Render the scene
    renderer.render(scene, camera);

    // Get the canvas and convert to data URL
    const canvas = renderer.domElement;
    const dataURL = canvas.toDataURL('image/png');

    // Create download link
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = filename || 'screenshot.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Enable or disable camera controls
   */
  public enableCameraControls(controls: OrbitControls, enabled: boolean): void {
    if (!controls) return;

    controls.enableRotate = enabled;
    controls.enablePan = enabled;
    controls.enableZoom = true; // Keep zoom enabled for usability

    // Maintain polar angle constraints regardless of control state
    controls.minPolarAngle = 0; // Top-down view
    controls.maxPolarAngle = Math.PI / 2; // Horizontal view, prevents seeing underneath
  }

  /**
   * Configure camera controls with specific settings
   */
  public configureCameraControls(
    controls: OrbitControls,
    config: {
      enableRotate?: boolean;
      enablePan?: boolean;
      enableZoom?: boolean;
      rotateSpeed?: number;
      zoomSpeed?: number;
      panSpeed?: number;
      minPolarAngle?: number;
      maxPolarAngle?: number;
      minDistance?: number;
      maxDistance?: number;
    }
  ): void {
    if (!controls) return;

    const {
      enableRotate = true,
      enablePan = true,
      enableZoom = true,
      rotateSpeed = 1.0,
      zoomSpeed = 1.2,
      panSpeed = 0.8,
      minPolarAngle = 0,
      maxPolarAngle = Math.PI / 2,
      minDistance = 0.1,
      maxDistance = 100,
    } = config;

    controls.enableRotate = enableRotate;
    controls.enablePan = enablePan;
    controls.enableZoom = enableZoom;
    controls.rotateSpeed = rotateSpeed;
    controls.zoomSpeed = zoomSpeed;
    controls.panSpeed = panSpeed;
    controls.minPolarAngle = minPolarAngle;
    controls.maxPolarAngle = maxPolarAngle;
    controls.minDistance = minDistance;
    controls.maxDistance = maxDistance;

    controls.update();
  }

  /**
   * Get camera position and rotation information
   */
  public getCameraInfo(camera: THREE.Camera): {
    position: THREE.Vector3;
    rotation: THREE.Euler;
    zoom?: number;
  } {
    const info = {
      position: camera.position.clone(),
      rotation: camera.rotation.clone(),
      zoom: undefined as number | undefined,
    };

    if (camera instanceof THREE.OrthographicCamera) {
      info.zoom = camera.zoom;
    }

    return info;
  }

  /**
   * Set camera position and rotation
   */
  public setCameraTransform(
    camera: THREE.Camera,
    position: THREE.Vector3,
    lookAt?: THREE.Vector3,
    zoom?: number
  ): void {
    if (!camera) return;

    camera.position.copy(position);

    if (lookAt) {
      camera.lookAt(lookAt);
    }

    if (zoom !== undefined && camera instanceof THREE.OrthographicCamera) {
      const orthoCamera = camera as THREE.OrthographicCamera;
      orthoCamera.zoom = Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
      orthoCamera.updateProjectionMatrix();
    }
  }
}

// Export singleton instance
export const cameraManager = new CameraManager();
